import React from 'react';
import { PerformanceFeeStructure } from '../../types/client';
import styles from './FeeStructure.module.css';
import commonStyles from './ClientCommon.module.css';
import {
    Box,
    FormHelperText,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { formatPercentage } from '@/utils/formatters';
import { Button, Tag } from '@/components/ui';

interface FeeStructureRowProps {
    index: number;
    structure: PerformanceFeeStructure;
    previousStructure?: PerformanceFeeStructure | null;
    onStructureChange: (index: number, field: keyof PerformanceFeeStructure, value: any) => void;
    onRemoveStructure: (index: number) => void;
    isEditMode: boolean;
    errors: any;
    hurdleTypeChoices: Array<{ value: string; label: string }>;
    hurdleBenchmarkChoices: Array<{ value: string; label: string }>;
}

export const FeeStructureRow: React.FC<FeeStructureRowProps> = ({
    index,
    structure,
    previousStructure,
    onStructureChange,
    onRemoveStructure,
    isEditMode,
    errors,
    hurdleTypeChoices,
    hurdleBenchmarkChoices,
}) => {
    const handleInputChange = (field: keyof PerformanceFeeStructure, value: any) => {
        onStructureChange(index, field, value);
    };

    const renderPreviousValue = (fieldName: keyof PerformanceFeeStructure) => {
        if (!previousStructure) return null;
        const previousValue = (previousStructure as any)[fieldName];
        const currentValue = (structure as any)[fieldName];

        if (previousValue === currentValue) return null;

        let displayValue = '';
        if (previousValue === null || previousValue === undefined || previousValue === '') {
            displayValue = 'EMPTY';
        } else if (fieldName === 'hurdle_type') {
            displayValue = hurdleTypeChoices.find(c => c.value === previousValue)?.label || String(previousValue);
        } else if (fieldName === 'hurdle_benchmark') {
            displayValue = hurdleBenchmarkChoices.find(c => c.value === previousValue)?.label || String(previousValue);
        } else if (fieldName === 'hurdle_value' || fieldName === 'fee_value') {
            displayValue = isNaN(Number(previousValue)) ? `${previousValue}%` : formatPercentage(previousValue);
        } else {
            displayValue = String(previousValue);
        }

        return <span className={commonStyles['previous-value']}>Previously: {displayValue}</span>;
    };

    if (!isEditMode) {
        return (
            <TableRow className={styles['fee-table-row']}>
                <TableCell sx={{ fontWeight: 500 }}>
                    <Box>
                        {structure.step_no}
                        {renderPreviousValue('step_no')}
                    </Box>
                </TableCell>
                <TableCell>
                    <Box>
                        <Tag>{structure.hurdle_type_display || structure.hurdle_type}</Tag>
                        {renderPreviousValue('hurdle_type')}
                    </Box>
                </TableCell>
                <TableCell>
                    <Box>
                        {structure.hurdle_benchmark_display || structure.hurdle_benchmark || '—'}
                        {renderPreviousValue('hurdle_benchmark')}
                    </Box>
                </TableCell>
                <TableCell align="right">
                    <Box>
                        {formatPercentage(structure.hurdle_value)}
                        {renderPreviousValue('hurdle_value')}
                    </Box>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                    <Box>
                        {formatPercentage(structure.fee_value)}
                        {renderPreviousValue('fee_value')}
                    </Box>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={styles['fee-table-row']}>
            <TableCell width="10%">
                <Box>
                    <TextField
                        type="number"
                        value={structure.step_no ?? ''}
                        onChange={(e) => {
                            const v = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                            handleInputChange('step_no', Number.isNaN(v) ? '' : v);
                        }}
                        variant="outlined"
                        size="small"
                        error={!!errors?.step_no}
                        fullWidth
                        placeholder="1"
                    />
                    {renderPreviousValue('step_no')}
                </Box>
            </TableCell>

            <TableCell width="20%">
                <Box>
                    <Select
                        value={structure.hurdle_type || ''}
                        onChange={(e) => handleInputChange('hurdle_type', e.target.value)}
                        variant="outlined"
                        size="small"
                        error={!!errors?.hurdle_type}
                        fullWidth
                        displayEmpty
                    >
                        <MenuItem value="" disabled>Select Type</MenuItem>
                        {hurdleTypeChoices.map(choice => (
                            <MenuItem key={choice.value} value={choice.value}>
                                {choice.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {renderPreviousValue('hurdle_type')}
                </Box>
            </TableCell>

            <TableCell width="25%">
                <Box>
                    <Select
                        value={structure.hurdle_benchmark || ''}
                        onChange={(e) => handleInputChange('hurdle_benchmark', e.target.value)}
                        disabled={structure.hurdle_type !== 'Benchmark'}
                        variant="outlined"
                        size="small"
                        error={!!errors?.hurdle_benchmark}
                        fullWidth
                        displayEmpty
                    >
                        <MenuItem value="" disabled>
                            <span style={{ opacity: 0.5 }}>Select Benchmark</span>
                        </MenuItem>
                        {hurdleBenchmarkChoices.map(choice => (
                            <MenuItem key={choice.value} value={choice.value}>
                                {choice.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errors?.hurdle_benchmark && (
                        <FormHelperText error sx={{ m: 0, mt: 0.5 }}>
                            {errors.hurdle_benchmark}
                        </FormHelperText>
                    )}
                    {renderPreviousValue('hurdle_benchmark')}
                </Box>
            </TableCell>

            <TableCell width="20%" align="right">
                <Box>
                    <TextField
                        type="number"
                        value={structure.hurdle_value !== undefined && structure.hurdle_value !== null ? structure.hurdle_value : ''}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const v = raw === '' ? '' : parseFloat(raw);
                            handleInputChange('hurdle_value', Number.isNaN(v) ? '' : v);
                        }}
                        disabled={structure.hurdle_type !== 'Value'}
                        variant="outlined"
                        size="small"
                        error={!!errors?.hurdle_value}
                        fullWidth
                        placeholder="0.00"
                        inputProps={{ style: { textAlign: 'right' } }}
                    />
                    {errors?.hurdle_value && (
                        <FormHelperText error sx={{ m: 0, mt: 0.5 }}>
                            {errors.hurdle_value}
                        </FormHelperText>
                    )}
                    {renderPreviousValue('hurdle_value')}
                </Box>
            </TableCell>

            <TableCell width="15%" align="right">
                <Box>
                    <TextField
                        type="number"
                        value={structure.fee_value !== undefined && structure.fee_value !== null ? structure.fee_value : ''}
                        onChange={(e) => {
                            const raw = e.target.value;
                            const v = raw === '' ? '' : parseFloat(raw);
                            handleInputChange('fee_value', Number.isNaN(v) ? '' : v);
                        }}
                        variant="outlined"
                        size="small"
                        error={!!errors?.fee_value}
                        fullWidth
                        placeholder="0.00"
                        inputProps={{ style: { textAlign: 'right' } }}
                    />
                    {errors?.fee_value && (
                        <FormHelperText error sx={{ m: 0, mt: 0.5 }}>
                            {errors.fee_value}
                        </FormHelperText>
                    )}
                    {renderPreviousValue('fee_value')}
                </Box>
            </TableCell>

            <TableCell width="5%" align="left">
                <Tooltip title="Remove step">
                    <Button
                        iconOnly
                        aria-label="Remove step"
                        onClick={() => onRemoveStructure(index)}
                    >
                        <DeleteIcon fontSize="small" />
                    </Button>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};

interface FeeStructureTableProps {
    structures: PerformanceFeeStructure[];
    previousStructures?: PerformanceFeeStructure[];
    onStructureChange: (index: number, field: keyof PerformanceFeeStructure, value: any) => void;
    onRemoveStructure: (index: number) => void;
    isEditMode: boolean;
    errors: any[];
    hurdleTypeChoices: Array<{ value: string; label: string }>;
    hurdleBenchmarkChoices: Array<{ value: string; label: string }>;
}

export const FeeStructureTable: React.FC<FeeStructureTableProps> = ({
    structures,
    previousStructures = [],
    onStructureChange,
    onRemoveStructure,
    isEditMode,
    errors,
    hurdleTypeChoices,
    hurdleBenchmarkChoices,
}) => {
    return (
        <TableContainer component={Paper} elevation={0} className={styles['fee-table-container']}>
            <Table size="small">
                <TableHead>
                    <TableRow className={styles['fee-table-head-row']}>
                        <TableCell>Step</TableCell>
                        <TableCell>Hurdle Type</TableCell>
                        <TableCell>Hurdle Benchmark</TableCell>
                        <TableCell align="right">Hurdle</TableCell>
                        <TableCell align="right">Fee</TableCell>
                        {isEditMode && <TableCell align="left">Actions</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {structures.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={isEditMode ? 6 : 5} align="center" sx={{ py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No performance fee steps configured.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        structures.map((structure, index) => {
                            const prev = previousStructures.find(p => p.step_no === structure.step_no);
                            return (
                                <FeeStructureRow
                                    key={index}
                                    index={index}
                                    structure={structure}
                                    previousStructure={prev}
                                    onStructureChange={onStructureChange}
                                    onRemoveStructure={onRemoveStructure}
                                    isEditMode={isEditMode}
                                    errors={errors[index] || {}}
                                    hurdleTypeChoices={hurdleTypeChoices}
                                    hurdleBenchmarkChoices={hurdleBenchmarkChoices}
                                />
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
